import { useEffect } from "react";
import { useHudStore } from "../store/hudStore";
import { ATTRS, ATTR_CAP, fmtDate, titleCase } from "../data/seed";
import { Badge, Button, Card, Stat } from "../design-system/components";
import Page from "../components/Page";
import type { DoneQuest } from "../types";

// The Quest Log (screen: "log"): every completed quest, newest first, grouped
// by the day it was done. Backed by GET /api/quests?status=done. Loads on mount
// so a deep link / refresh to /log works.

const attrColor = (attribute: string): string =>
  ATTRS.find((a) => a.name === (ATTR_CAP[attribute] ?? attribute))?.color ?? "var(--accent)";

export default function QuestLog() {
  const st = useHudStore();

  useEffect(() => { void st.loadLog(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const log = st.questLog;
  const totalXp = log.reduce((t, q) => t + (q.xp || 0), 0);
  const unavailable = !!st.logError && log.length === 0;

  // Group by done_at (already newest-first from the API), preserving order.
  const groups: [string, DoneQuest[]][] = [];
  for (const q of log) {
    const key = q.done_at || "None";
    const last = groups[groups.length - 1];
    if (last && last[0] === key) last[1].push(q);
    else groups.push([key, [q]]);
  }

  return (
    <Page back="Back to the HUD" onBack={st.closeLog}>
      <Card className="detail-hero history-hero">
        <span className="detail-identity-icon"><i aria-hidden="true" className="fa-solid fa-clock-rotate-left" /></span>
        <div className="detail-head-body"><h1 className="detail-heading detail-title">Quest log</h1><p className="detail-copy">Everything you've cleared, newest first.</p></div>
        <div className="history-summary"><Stat label="Completed" value={unavailable ? <span className="history-unavailable">Unavailable</span> : log.length} /><Stat label="XP earned" value={unavailable ? <span className="history-unavailable">Unavailable</span> : totalXp.toLocaleString()} tone="accent" /></div>
      </Card>
      {st.logError && <Card className="history-error" role="alert" title="History unavailable" description={st.logError} actions={<Button onClick={() => void st.loadLog()} loading={st.logBusy} icon="fa-solid fa-arrows-rotate">{st.logBusy ? "Trying again…" : "Retry history"}</Button>} />}
      {st.logBusy && log.length === 0 ? (
        <Card className="history-feedback" role="status"><i className="fa-solid fa-circle-notch ds-spinner" aria-hidden="true" /><p>Loading your history…</p></Card>
      ) : log.length === 0 && !st.logError ? (
        <Card className="history-feedback" title="No completed quests yet" description="Finish a quest and it'll show up here, with the XP you earned." actions={<Button variant="primary" onClick={st.closeLog}>Browse quests</Button>} />
      ) : groups.map(([date, quests]) => (
        <Card key={date} title={fmtDate(date) || date} actions={<Badge>{quests.length} · {quests.reduce((total, quest) => total + (quest.xp || 0), 0)} XP</Badge>}>
          {quests.map((q) => (
            <div key={q.id} className="history-row">
              <span className="history-check" aria-hidden="true"><i className="fa-solid fa-check" /></span>
              <span className="history-title">{q.title}</span>
              <span className="detail-xp history-xp">+{q.xp || 0} XP</span>
              <span className="history-metadata"><Badge><b className="detail-status-dot" style={{ background: attrColor(q.attribute) }} />{q.domain}</Badge>{q.quest_type && <Badge>{titleCase(q.quest_type)}</Badge>}</span>
            </div>
          ))}
        </Card>
      ))}
    </Page>
  );
}
